import { useState } from "react";
import styled from "styled-components";
import { getLocale } from "react-i18nify";

import device from "src/styles/device";
import font from "src/styles/font";

import {
  Grid,
  Box,
  FormControl,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
} from "@mui/material";

import Button from "src/components/core/Button";

import AddIcon from "@mui/icons-material/Add";

import useModal from "src/hooks/useModal";
import useDeviceSize from "src/hooks/useDeviceSize";
import { convertPrice } from "src/common";

import ProductDataGrid from "./Product";
import ServiceDataGrid from "./Service";
import OtherDataGrid from "./Other";
import DetailDataGrid from "./Detail";

import ProductSheet from "src/modules/quotation-draft/CreateQuoDraft/ProductSheet";
import ModalConfirm from "src/components/ui/ModalConfirm";

const WrapperContainer = styled.div`
  width: 100%;

  @media ${device.tabletL} {
    padding: 0 40px;
  }
  @media ${device.mobileL} {
    padding: 0 24px;
  }
`;

const SpanTitle = styled.div`
  font-size: ${font.size.lg};
  color: var(--secondary-color);
  font-weight: 400;

  span {
    font-size: ${font.size.lg};
    font-weight: 400;
  }
`;

const DocumentSection = styled.div`
  width: 100%;
  margin-top: 24px;
  background-color: var(--white-color);
  border-radius: 24px;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);

  @media ${device.tabletL} {
    padding-bottom: 0;
    margin-bottom: 24px;
    box-shadow: none;
    /* border-radius: 24px; */
  }
`;

const PriceSummerySection = styled.div`
  width: 100%;
  margin-top: 24px;
  /* padding: 24px 24px 40px; */
  border-radius: 24px;
  background-color: var(--darkwhite-color);
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.1);

  @media ${device.tabletL} {
    /* padding: 16px; */
    border-radius: 24px;
    margin-bottom: 24px;
    /* box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.10); */
  }
`;

const GroupPrice = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// const PriceLabel = styled.div`
//   font-size: ${font.size.sm};
//   font-weight: 400;
//   color: var(--secondary-color);
// `

const PriceText = styled.div`
  font-size: ${font.size.md};
  font-weight: 400;
  color: ${({ total }) =>
    total ? "var(--primary-color)" : "var(--secondary-color)"};

  @media ${device.tabletL} {
    font-size: ${({ price }) => (price ? font.size.md : font.size.sm)};
  }
`;

const BreakSectionLine = styled.div`
  width: 100%;
  margin-top: 24px;

  @media ${device.tabletL} {
    width: calc(100% + 80px);
    margin: 24px -40px;
    border-top: 1px solid var(--lightgrey-color);
  }
  @media ${device.mobileL} {
    width: calc(100% + 48px);
    margin: 24px -24px;
    border-top: 1px solid var(--lightgrey-color);
  }
`;

const DocumentGrid = ({
  isDetail = false, // change to test
  formErrors,
  formData,
  onChange,
  dataProductList = [],
  dataServiceType = [],
  // onAppendElement, // add default product
  // onAppendData, // add multiple products
  // onRemoveElement,
  onAppendProduct,
  onAppendProductData,
  onRemoveProduct,
  onAppendService,
  onAppendServiceData,
  onRemoveService,
  onAppendOther,
  onAppendOtherData,
  onRemoveOther,
}) => {
  const initialConfirm = {
    visible: false,
    title: "",
    confirmFunc: () => {},
  };
  const [confirmInfo, setConfirmInfo] = useState(initialConfirm);
  const [sheetType, setSheetType] = useState("");

  const [deviceWidth] = useDeviceSize();

  const { getModalState, open, close } = useModal();

  const handleOnAppendData = async (data) => {
    if (sheetType === "service") {
      onAppendServiceData(data);
    } else if (sheetType === "product") {
      onAppendProductData(data);
    } else {
      onAppendOtherData(data);
    }

    await onChange("", "", "");
  };

  const getDatagridComponent = () => {
    switch (formData?.document_type) {
      case "product":
        return (
          <>
            <SpanTitle>
              เพิ่มรายการสินค้าบนเว็บไซต์
              <span style={{ color: "var(--primary-color)" }}>*</span>
            </SpanTitle>

            <DocumentSection>
              <ProductDataGrid
                formErrors={formErrors}
                formData={formData}
                onChange={onChange}
                onAppendElement={onAppendProduct}
                onRemoveElement={onRemoveProduct}
                dataProductList={dataProductList}
                initialConfirm={initialConfirm}
                setConfirmInfo={setConfirmInfo}
              />
            </DocumentSection>

            {/* Add product's Button mobile & tablet */}
            {deviceWidth <= 835 && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Button
                  variant="secondary"
                  size="large"
                  startIcon={<AddIcon />}
                  sx={{
                    width: "100%",
                    maxWidth: "350px",
                    py: "12px",
                    borderRadius: 4,
                    "&.MuiButton-outlined": {
                      border: "1px #e04132 solid",
                    },
                  }}
                  onClick={() => {
                    open(`quotation_list.add-product-bottom-sheet`);
                    setSheetType("product");
                  }}
                >
                  Add Item on website
                </Button>
              </Box>
            )}

            <BreakSectionLine />

            <SpanTitle>
              เพิ่มรายการอื่นๆ
              <span style={{ color: "var(--primary-color)" }}>*</span>
            </SpanTitle>

            <DocumentSection>
              <OtherDataGrid
                formErrors={formErrors}
                formData={formData}
                onChange={onChange}
                dataProductList={dataProductList}
                dataServiceType={dataServiceType}
                onAppendElement={onAppendOther}
                onRemoveElement={onRemoveOther}
                initialConfirm={initialConfirm}
                setConfirmInfo={setConfirmInfo}
              />
            </DocumentSection>

            {/* Add product's Button mobile & tablet */}
            {deviceWidth <= 835 && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Button
                  variant="secondary"
                  size="large"
                  startIcon={<AddIcon />}
                  sx={{
                    width: "100%",
                    maxWidth: "350px",
                    py: "12px",
                    borderRadius: 4,
                    "&.MuiButton-outlined": {
                      border: "1px #e04132 solid",
                    },
                  }}
                  onClick={() => {
                    open(`quotation_list.add-product-bottom-sheet`);
                    setSheetType("other");
                  }}
                >
                  Add Item other
                </Button>
              </Box>
            )}
          </>
        );
      case "service":
        return (
          <>
            <SpanTitle>
              เพิ่มการบริการช่าง
              <span style={{ color: "var(--primary-color)" }}>*</span>
            </SpanTitle>

            <DocumentSection>
              <ServiceDataGrid
                isDetail={isDetail}
                formErrors={formErrors}
                formData={formData}
                onChange={onChange}
                onAppendElement={onAppendService}
                onRemoveElement={onRemoveService}
                dataServiceType={dataServiceType}
                initialConfirm={initialConfirm}
                setConfirmInfo={setConfirmInfo}
              />
            </DocumentSection>

            {/* Add service's Button for mobile & tablet */}
            {deviceWidth <= 835 && !isDetail && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Button
                  variant="secondary"
                  size="large"
                  startIcon={<AddIcon />}
                  sx={{
                    width: "100%",
                    maxWidth: "350px",
                    py: "12px",
                    borderRadius: 4,
                    "&.MuiButton-outlined": {
                      border: "1px #e04132 solid",
                    },
                  }}
                  onClick={() => {
                    open(`quotation_list.add-product-bottom-sheet`);
                    setSheetType("service");
                  }}
                >
                  Add Service
                </Button>
              </Box>
            )}
          </>
        );
      // case "other":
      //   return (
      //     <OtherDataGrid
      //       isDetail={isDetail}
      //       formErrors={formErrors}
      //       formData={formData}
      //       onChange={onChange}
      //       onAppendElement={onAppendOther}
      //       onRemoveElement={onRemoveOther}
      //       initialConfirm={initialConfirm}
      //       setConfirmInfo={setConfirmInfo}
      //     />
      //   );
      default:
        return null;
    }
  };

  return (
    <>
      {!isDetail ? (
        <Grid
          container
          sx={{
            m: 0,
            mb: 3,
            px: { xs: 3, sm: 5, md: 3 },
            py: { xs: 3, md: 5 },
            width: "100%",
            borderRadius: { md: "24px" },
            background: "var(--white-color)",
            boxShadow: {
              md: "0px 0px 10px 0px rgba(0, 0, 0, 0.10)",
            },
            borderBottom: {
              xs: "10px var(--darkwhite-color) solid",
              md: "none",
            },
          }}
        >
          <Grid item xs={12}>
            <FormControl sx={{ width: "100%" }}>
              <FormLabel
                id="customer-type-radio-group-label"
                sx={{
                  fontSize: font.size.lg,
                  color: "var(--secondary-color)",
                  mb: 2,
                  span: {
                    color: "var(--primary-color)",
                  },
                  "&.Mui-focused": {
                    color: "var(--secondary-color)",
                  },
                }}
              >
                {getLocale() === "th"
                  ? "เพิ่มประเภทใบเสนอราคา"
                  : "เพิ่มประเภทใบเสนอราคา"}
                <span>*</span>
              </FormLabel>

              <RadioGroup
                row
                aria-labelledby="customer-type-radio-group-label"
                name="customer-type-radio-group"
                sx={{
                  px: {
                    sm: 0,
                    md: 4,
                  },
                }}
                value={formData?.document_type}
                onChange={(e) => onChange(e, `document_type`)}
              >
                <FormControlLabel
                  value="product"
                  sx={{
                    flex: "0 0 50%",
                    width: "50%",
                    m: 0,
                    "&.MuiRadio-root": {
                      width: "auto",
                    },
                  }}
                  control={
                    <Radio
                      sx={{
                        fontSize: font.size.md,
                        color: "var(--primary-color)",
                        "&.Mui-checked": {
                          color: "var(--primary-color)",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 22,
                        },
                      }}
                    />
                  }
                  label={
                    getLocale() === "th"
                      ? "ใบเสนอราคาสินค้า"
                      : "ใบเสนอราคาสินค้า"
                  }
                />

                <FormControlLabel
                  value="service"
                  sx={{
                    flex: "0 0 50%",
                    width: "50%",
                    m: 0,
                    "&.MuiRadio-root": {
                      width: "auto",
                    },
                  }}
                  control={
                    <Radio
                      sx={{
                        fontSize: font.size.md,
                        color: "var(--primary-color)",
                        "&.Mui-checked": {
                          color: "var(--primary-color)",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 22,
                        },
                      }}
                    />
                  }
                  label={
                    getLocale() === "th" ? "ใบเสนอราคาช่าง" : "ใบเสนอราคาช่าง"
                  }
                />

                {/* <FormControlLabel
                  value="other"
                  sx={{
                    flex: "0 0 50%",
                    width: "50%",
                    m: 0,
                    "&.MuiRadio-root": {
                      width: "auto",
                    },
                  }}
                  control={
                    <Radio
                      sx={{
                        fontSize: font.size.md,
                        color: "var(--primary-color)",
                        "&.Mui-checked": {
                          color: "var(--primary-color)",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 22,
                        },
                      }}
                    />
                  }
                  label={getLocale() === "th" ? "รายการอื่นๆ" : "รายการอื่นๆ"}
                /> */}
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
      ) : null}

      <WrapperContainer>
        {isDetail ? (
          <>
            <SpanTitle>
              {formData?.document_type === "service"
                ? `รายการบริการช่าง`
                : `รายการสินค้า`}
            </SpanTitle>

            <DocumentSection>
              <DetailDataGrid formData={formData} />
            </DocumentSection>
          </>
        ) : (
          <>{getDatagridComponent()}</>
        )}

        {/* <DocumentSection>
          {isDetail
            ? <DetailDataGrid formData={formData} /> 
            : getDatagridComponent()
          }
        </DocumentSection> */}

        {/* Add product/service/other button mobile & tablet Button */}
        {/* {deviceWidth <= 835 && !isDetail && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              // variant={
              //   formData?.quotation_list[0].product_choice_id === null &&
              //     formData?.quotation_list[0].service_type === null &&
              //     formData?.quotation_list[0].other_code === "" &&
              //     formData?.quotation_list[0].other_text === ""
              //     ? "contained"
              //     : "outlined"
              // }
              variant="secondary"
              size="large"
              startIcon={<AddIcon />}
              sx={{
                width: "100%",
                maxWidth: "350px",
                py: "12px",
                borderRadius: 4,
                "&.MuiButton-outlined": {
                  border: "1px #e04132 solid",
                },
              }}
              onClick={() => {
                open(
                  `quotation_list.add-product-bottom-sheet`
                );
              }}
            >
              {formData?.document_type === "service"
                ? `Add Service`
                : `Add Product`}
            </Button>
          </Box>
        )} */}

        {/* Price summery in create */}
        {!isDetail ? (
          <PriceSummerySection>
            <Grid
              container
              sx={{
                m: 0,
                width: "100%",
                px: {
                  lg: 5,
                  md: 3,
                  xs: 2,
                },
                py: {
                  md: 3,
                  xs: 2,
                },
                ".MuiGrid-item": {
                  mb: {
                    md: 2,
                    xs: 1,
                  },
                  ":last-child": {
                    mb: 0,
                  },
                },
              }}
            >
              <Grid item xs={12}>
                <GroupPrice>
                  <PriceText>รวมเงิน</PriceText>

                  <PriceText price={true}>
                    {convertPrice(formData?.total_price || 0)}
                  </PriceText>
                </GroupPrice>
              </Grid>

              <Grid item xs={12}>
                <GroupPrice>
                  <PriceText>ส่วนลด</PriceText>

                  <PriceText price={true}>
                    {convertPrice(formData?.discount || 0)}
                  </PriceText>
                </GroupPrice>
              </Grid>

              <Grid item xs={12}>
                <GroupPrice>
                  <PriceText>มูลค่าหลังหักส่วนลด</PriceText>

                  <PriceText price={true}>
                    {convertPrice(
                      +formData?.total_price - +formData?.discount || 0,
                    )}
                  </PriceText>
                </GroupPrice>
              </Grid>

              <Grid item xs={12}>
                <GroupPrice>
                  <PriceText>ราคารวม Vat7%</PriceText>

                  <PriceText price={true}>
                    +{convertPrice(formData?.vat || 0)}
                  </PriceText>
                </GroupPrice>
              </Grid>

              <Grid item xs={12}>
                <GroupPrice>
                  <PriceText>จำนวนเงินทั้งสิ้น</PriceText>

                  <PriceText price={true} total={true}>
                    {convertPrice(formData?.total_vat || 0)}
                  </PriceText>
                </GroupPrice>
              </Grid>
            </Grid>
          </PriceSummerySection>
        ) : null}

        {/* Add product/service/other tablet-mobile Botton sheet */}
        <ProductSheet
          // quoList={formData?.quotation_list}
          quoList={
            sheetType === "service"
              ? formData?.service_list
              : sheetType === "product"
                ? formData?.product_list
                : formData?.other_list
          }
          dataProductList={dataProductList}
          dataServiceType={dataServiceType}
          docType={sheetType}
          isOpen={
            getModalState(`quotation_list.add-product-bottom-sheet`)?.isOpen
          }
          onClose={() => {
            close(`quotation_list.add-product-bottom-sheet`);
            setSheetType("");
          }}
          // onAppendData={onAppendData}
          onAppendData={handleOnAppendData}
          // onRemoveElement={onRemoveElement}
          onRemoveElement={
            sheetType === "service"
              ? onRemoveService
              : sheetType === "product"
                ? onRemoveProduct
                : onRemoveOther
          }
        />
      </WrapperContainer>

      {confirmInfo.visible ? (
        <ModalConfirm
          title={confirmInfo.title}
          callBack={(value) =>
            value === false
              ? setConfirmInfo(initialConfirm)
              : confirmInfo?.confirmFunc()
          }
          textCancel={getLocale() === "th" ? "ยกเลิก" : "cancel"}
          textConfirm={getLocale() === "th" ? "ลบ" : "delete"}
        />
      ) : null}
    </>
  );
};

export default DocumentGrid;
